/**
 * Cash Flow Summary — /finance/reports/cash-flow
 *
 * Shows cash inflows (receipts) vs outflows (payments) for a period.
 * Calls GET /finance/reports/cash-flow
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeftRight, RefreshCw, Download, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

// ── Types ─────────────────────────────────────────────────────────────────────

interface CashFlowData {
  reportType: string;
  period: { from: string; to: string };
  inflows:     number;
  outflows:    number;
  netCashFlow: number;
}

function fmt(n: number) {
  return new Intl.NumberFormat('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

function thisYear() {
  const now = new Date();
  return { from: `${now.getFullYear()}-01-01`, to: now.toISOString().split('T')[0]! };
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function CashFlowPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData]       = useState<CashFlowData | null>(null);
  const [from, setFrom]       = useState(thisYear().from);
  const [to, setTo]           = useState(thisYear().to);
  const loadedRef = useRef(false);

  const fetchData = useCallback(async (f: string, t: string) => {
    setLoading(true);
    try {
      const res = await api.get<CashFlowData>(`/finance/reports/cash-flow?from=${f}&to=${t}`);
      setData(res ?? null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(() => {
    loadedRef.current = false;
    fetchData(from, to);
  }, [fetchData, from, to]);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    fetchData(from, to);
  }, [fetchData, from, to]);

  const net   = data?.netCashFlow ?? 0;
  const NetIcon = net > 0 ? TrendingUp : net < 0 ? TrendingDown : Minus;
  const netColor = net > 0 ? 'text-green-700' : net < 0 ? 'text-red-600' : 'text-muted-foreground';

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <ArrowLeftRight className="h-6 w-6 text-cyan-600" />
          <div>
            <h1 className="text-xl font-semibold">Cash Flow</h1>
            <p className="text-sm text-muted-foreground">Receipts vs payments for the selected period</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input type="date" value={from} onChange={e => { setFrom(e.target.value); loadedRef.current = false; }}
            className="border rounded px-2 py-1 text-sm" />
          <input type="date" value={to} onChange={e => { setTo(e.target.value); loadedRef.current = false; }}
            className="border rounded px-2 py-1 text-sm" />
          <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm"><Download className="h-4 w-4 mr-2" /> Export</Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading…</div>
      ) : !data ? (
        <div className="text-center py-12 text-muted-foreground">No cash flow data found.</div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-4 border-green-200 bg-green-50">
              <div className="text-xs font-medium uppercase tracking-wide text-green-700 mb-1">Cash Inflows</div>
              <div className="text-2xl font-bold font-mono text-green-800">{fmt(data.inflows)}</div>
              <div className="text-xs text-green-600 mt-1">Receipts from customers</div>
            </Card>
            <Card className="p-4 border-orange-200 bg-orange-50">
              <div className="text-xs font-medium uppercase tracking-wide text-orange-700 mb-1">Cash Outflows</div>
              <div className="text-2xl font-bold font-mono text-orange-800">{fmt(data.outflows)}</div>
              <div className="text-xs text-orange-600 mt-1">Payments to vendors</div>
            </Card>
            <Card className={`p-4 border ${net >= 0 ? 'border-blue-200 bg-blue-50' : 'border-red-200 bg-red-50'}`}>
              <div className={`text-xs font-medium uppercase tracking-wide mb-1 ${net >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                Net Cash Flow
              </div>
              <div className={`text-2xl font-bold font-mono flex items-center gap-1 ${netColor}`}>
                <NetIcon className="h-5 w-5" />
                {fmt(Math.abs(net))}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {net >= 0 ? 'Net positive' : 'Net negative'} for the period
              </div>
            </Card>
          </div>

          {/* Statement table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Line Item</TableHead>
                    <TableHead className="text-right pr-6">Amount (SGD)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell>Cash Receipts from Customers</TableCell>
                    <TableCell className="text-right font-mono text-green-700 pr-6">{fmt(data.inflows)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Cash Payments to Vendors</TableCell>
                    <TableCell className="text-right font-mono text-orange-700 pr-6">({fmt(data.outflows)})</TableCell>
                  </TableRow>
                  <TableRow className="bg-muted/40 font-semibold border-t-2">
                    <TableCell>Net Cash from Operations</TableCell>
                    <TableCell className={`px-4 py-3 text-right font-mono pr-6 ${netColor}`}>{fmt(net)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

